import React from 'react';
import Styled from 'styled-components';
import PropTypes from 'prop-types';

export const SIZE = {
	STANDARD: 'standard',
	SMALL: 'small',
	MEDIUM_SMALL: 'medium_small',
	BIG: 'big',
	TALL: 'tall',
};

export const FONT_SIZE = {
	STANDARD: 'standard',
	LARGE: 'large',
};

export const VARIANT = {
	PURPLE_FILLED: 'purple_filled',
	PURPLE_OUTLINE: 'purple_outline',
	GREEN_FILLED: 'green_filled',
	VERY_LIGHT_GRAY_OUTLINE: 'very_light_gray_outline',
	VERY_LIGHT_GRAY_FILLED: 'very_light_gray_filled',
	RED_FILLED: 'red_filled',
	RED_OUTLINE: 'red_outline',
};

export const ICON_POSITION = {
	FIRST: 'first',
	LAST: 'last',
	CENTER: 'center',
};

export const Button = ({
	showText,
	isSquare,
	title,
	variant,
	disabled,
	onClick,
	userpilot,
	iconPosition,
	icon,
	size,
	fontSize,
	children,
	fullWidth,
	cy,
}) => {
	/**
	 * proxy for the onClick of the button
	 */
	const handleClick = e => {
		onClick(e);
	};

	const getIconColor = () => {
		switch (variant) {
			case VARIANT.RED_FILLED:
			case VARIANT.GREEN_FILLED:
			case VARIANT.VERY_LIGHT_GRAY_FILLED:
			case VARIANT.PURPLE_FILLED:
				return '#ffffff';
			case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
				return '#535353';
			default:
				return '#ffffff';
		}
	};

	return (
		<StyledButton
			title={title}
			showText={showText}
			isSquare={isSquare}
			fullWidth={fullWidth}
			variant={variant}
			disabled={disabled}
			size={size}
			fontSize={fontSize}
			onClick={handleClick}
			data-userpilot={userpilot}
			data-cy={cy ? cy : ''}
		>
			{iconPosition === ICON_POSITION.FIRST && (
				<IconWrapper noMargin={!showText || !children} iconPosition={iconPosition}>
					{icon(getIconColor())}
				</IconWrapper>
			)}
			{showText ? <span>{children}</span> : null}
			{iconPosition === ICON_POSITION.LAST && (
				<IconWrapper noMargin={!showText || !children} iconPosition={iconPosition}>
					{icon(getIconColor())}
				</IconWrapper>
			)}
		</StyledButton>
	);
};

Button.propTypes = {
	/**
	 *
	 */
	showText: PropTypes.bool,
	/**
	 * Text to be displayed on the button
	 */
	children: PropTypes.string,
	/**
	 * if fullWidth, the button width is controlled by the parent element
	 */
	fullWidth: PropTypes.bool,
	/**
	 * Predefined style of the button. accessed at Button.VARIANT...
	 */
	variant: PropTypes.oneOf([
		VARIANT.PURPLE_FILLED,
		VARIANT.PURPLE_OUTLINE,
		VARIANT.GREEN_FILLED,
		VARIANT.VERY_LIGHT_GRAY_FILLED,
		VARIANT.VERY_LIGHT_GRAY_OUTLINE,
		VARIANT.RED_FILLED,
		VARIANT.RED_OUTLINE,
	]),
	/**
	 * Disables the button
	 */
	disabled: PropTypes.bool,
	/**
	 * position of the icon, center is for no text (Work in progress). no input = no icon
	 */
	iconPosition: PropTypes.oneOf([ICON_POSITION.FIRST, ICON_POSITION.CENTER, ICON_POSITION.LAST]),
	/**
	 * The icon to be displayed.
	 */
	icon: PropTypes.func,
	/**
	 * Title of button
	 */
	title: PropTypes.string,
	/**
	 * onClick handler
	 */
	onClick: PropTypes.func,
	/**
	 *	userpilot tag
	 */
	userpilot: PropTypes.string,
	/**
	 * size of the button - accessed at Button.SIZE
	 */
	size: PropTypes.oneOf([SIZE.STANDARD, SIZE.SMALL, SIZE.MEDIUM_SMALL]),
	/**
	 * size of the font - accessed at Button.FONT_SIZE
	 */
	fontSize: PropTypes.oneOf([FONT_SIZE.STANDARD, FONT_SIZE.LARGE]),
	/**
	 *
	 */
	isSquare: PropTypes.bool,
	/**
	 * Cypress id
	 */
	cy: PropTypes.string,
};

Button.VARIANT = VARIANT;
Button.SIZE = SIZE;
Button.FONT_SIZE = FONT_SIZE;
Button.ICON_POSITION = ICON_POSITION;
Button.defaultProps = {
	onClick: () => false,
	iconPosition: 'center',
	size: SIZE.STANDARD,
	fontSize: FONT_SIZE.STANDARD,
	showText: true,
	isSquare: false,
};

const IconWrapper = Styled.div`
	margin-right: ${props => (props.iconPosition === 'first' && !props.noMargin ? '8px' : 0)};
	margin-left: ${props => (props.iconPosition === 'last' && !props.noMargin ? '8px' : 0)};
`;

const StyledButton = Styled.button`
	display: flex;
	align-items: center;
	cursor: pointer;
	justify-content: center;
	box-sizing: border-box;
	box-shadow: none;
	outline: none;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	padding: ${props => (props.showText && !props.isSquare ? '0 16px' : '0 0')};
	font-weight: 550;
	font-size: ${props => {
		if (props.fontSize) {
			switch (props.fontSize) {
				case FONT_SIZE.LARGE:
					return '12px';
				case FONT_SIZE.STANDARD:
					return '10px';
			}
		} else {
			switch (props.size) {
				case SIZE.TALL:
				case SIZE.STANDARD:
					return '10px';
				case SIZE.SMALL:
					return '9px';
			}
		}
	}};

	width: ${props => {
		if (props.fullWidth) return '100%';
		if (props.showText && !props.isSquare) return 'auto';
		switch (props.size) {
			case SIZE.TALL:
			case SIZE.STANDARD:
				return '30px';
			case SIZE.MEDIUM_SMALL:
				return '24px';
			case SIZE.SMALL:
				return '22px';
			default:
				return '30px';
		}
	}};
	background-color: ${props => {
		switch (props.variant) {
			case VARIANT.PURPLE_FILLED:
				return props.theme.colors.forecastPurple;
			case VARIANT.GREEN_FILLED:
				return props.theme.colors.green;
			case VARIANT.VERY_LIGHT_GRAY_FILLED:
				return props.theme.colors.mediumVeryLightGray;
			case VARIANT.RED_OUTLINE:
			case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
			case VARIANT.PURPLE_OUTLINE:
				return props.theme.colors.white;
			case VARIANT.RED_FILLED:
				return props.theme.colors.warningRed;
		}
	}};
	color: ${props => {
		switch (props.variant) {
			case VARIANT.PURPLE_FILLED:
			case VARIANT.GREEN_FILLED:
			case VARIANT.VERY_LIGHT_GRAY_FILLED:
			case VARIANT.RED_FILLED:
				return props.theme.colors.white;
			case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
				return props.theme.colors.textGray;
			case VARIANT.PURPLE_OUTLINE:
				return props.theme.colors.forecastPurple;
			case VARIANT.RED_OUTLINE:
				return props.theme.colors.warningRed;
		}
	}};
	height: ${props => {
		switch (props.size) {
			case SIZE.STANDARD:
				return '30px';
			case SIZE.MEDIUM_SMALL:
				return '24px';
			case SIZE.SMALL:
				return '22px';
			case SIZE.TALL:
				return '40px';
		}
	}};
	border:  ${props => {
		switch (props.variant) {
			case VARIANT.PURPLE_OUTLINE:
			case VARIANT.RED_OUTLINE:
				return '2px';
			default:
				return '1px';
		}
	}};
	border-style: solid;
	border-color: ${props => {
		switch (props.variant) {
			case VARIANT.GREEN_FILLED:
				return props.theme.colors.green;
			case VARIANT.VERY_LIGHT_GRAY_FILLED:
				return props.theme.colors.mediumVeryLightGray;
			case VARIANT.PURPLE_FILLED:
			case VARIANT.PURPLE_OUTLINE:
				return props.theme.colors.forecastPurple;
			case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
				return '#ebebeb';
			case VARIANT.RED_FILLED:
			case VARIANT.RED_OUTLINE:
				return props.theme.colors.warningRed;
		}
	}};
	
	&:hover:enabled {
		opacity: ${props => {
			switch (props.variant) {
				case VARIANT.GREEN_FILLED:
				case VARIANT.VERY_LIGHT_GRAY_FILLED:
				case VARIANT.PURPLE_FILLED:
				case VARIANT.PURPLE_OUTLINE:
				case VARIANT.RED_FILLED:
				case VARIANT.RED_OUTLINE:
					return 0.8;
				case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
					return 1;
			}
		}};
		border-color: ${props => {
			switch (props.variant) {
				case VARIANT.GREEN_FILLED:
					return props.theme.colors.green;
				case VARIANT.VERY_LIGHT_GRAY_FILLED:
					return props.theme.colors.mediumVeryLightGray;
				case VARIANT.PURPLE_FILLED:
				case VARIANT.PURPLE_OUTLINE:
					return props.theme.colors.forecastPurple;
				case VARIANT.VERY_LIGHT_GRAY_OUTLINE:
					return props.theme.colors.mediumLightGray;
				case VARIANT.RED_FILLED:
				case VARIANT.RED_OUTLINE:
					return props.theme.colors.warningRed;
			}
		}};
	}
	&:disabled {
		//Red butons when disabled should look like the same as white buttons
		border-style: solid;
		border-width:${props => {
			switch (props.variant) {
				case VARIANT.RED_FILLED:
				case VARIANT.RED_OUTLINE:
					return '1px';
			}
		}};
		border-color: ${props => {
			switch (props.variant) {
				case VARIANT.RED_FILLED:
				case VARIANT.RED_OUTLINE:
					return '#ebebeb';
			}
		}};

		color: ${props => {
			switch (props.variant) {
				case VARIANT.RED_FILLED:
				case VARIANT.RED_OUTLINE:
					return props.theme.colors.textGray;
			}
		}};
		

		opacity: 0.5;
		cursor: default;
	}
`;
